import { v4 as uuidv4 } from 'uuid';
import Module from '@/store/resource/common/module';
import CustomAgents from '@/store/resource/customAgents';
import api from '@/factories/api';

const TOOL_META = {
  message_sms: { group: 'Messaging', icon: 'sms' },
  send_email: { group: 'Messaging', icon: 'email' },
  notify_stakeholders: { group: 'Messaging', icon: 'notifications_active' },
  send_form: { group: 'Contact Data', icon: 'assignment' },
  update_contact_field: { group: 'Contact Data', icon: 'edit_note' },
  lookup_contacts: { group: 'Contact Data', icon: 'manage_search' },
  add_to_campaign: { group: 'Campaigns', icon: 'campaign' },
  create_task: { group: 'Tasks', icon: 'task_alt' },
  add_to_calendar: { group: 'Scheduling', icon: 'event' },
  search_portal: { group: 'Intelligence', icon: 'travel_explore' },
  mcp: { group: 'MCP', icon: 'extension' },
};

function emptyForm() {
  return {
    name: '',
    icon: 'smart_toy',
    description: '',
    instructions: '',
    goal: '',
    stop_conditions: '',
    status: 'active',
    tools: [],
    teams: [],
    model: '',
  };
}

function normalizeTool(tool, configSchema = {}) {
  const meta = TOOL_META[tool.name] || {};
  return {
    tool_uuid: tool.tool_uuid,
    name: tool.name,
    label: tool.label || tool.name,
    description: tool.description,
    allows_multiple_instances: Boolean(tool.allows_multiple_instances),
    group: meta.group || 'General',
    icon: meta.icon || 'build',
    configSchema,
  };
}

function normalizeToolInstance(t) {
  return {
    instance_uuid: t.instance_uuid || uuidv4(),
    tool_uuid: t.tool_uuid,
    config: t.config || {},
  };
}

function normalizeAgentDraft(agent) {
  if (!agent) return emptyForm();
  return {
    ...agent,
    tools: (agent.tools || []).map(normalizeToolInstance),
  };
}

function normalizeAvailableTools(catalog = [], schemas = {}) {
  return catalog
    .filter((tool) => !tool.system)
    .map((tool) => normalizeTool(tool, schemas[tool.name]?.configSchema));
}

function normalizePayload(payload = {}) {
  const normalized = { ...payload };
  if (
    Object.prototype.hasOwnProperty.call(normalized, 'instructions') &&
    typeof normalized.instructions === 'string' &&
    normalized.instructions.trim() === ''
  ) {
    delete normalized.instructions;
  }
  return normalized;
}

const module = new Module(new CustomAgents());

module.add('state', {
  draft: null,
  availableTools: [],
});

module.add('mutations', {
  setDraft(state, agent) {
    state.draft = normalizeAgentDraft(agent);
  },
  setDraftField(state, { key, value }) {
    state.draft = { ...(state.draft || emptyForm()), [key]: value };
  },
  setAvailableTools(state, tools) {
    state.availableTools = tools;
  },
});

module.add('actions', {
  async fetchAll({ dispatch }) {
    return dispatch('load');
  },

  async fetchOne({ dispatch }, id) {
    return dispatch('loadOne', { path: id });
  },

  async create({ dispatch }, payload) {
    return dispatch('createOne', normalizePayload(payload));
  },

  async update({ dispatch, getters }, { id, payload }) {
    const current = getters.byId(id) || {};
    const normalized = normalizePayload(payload);
    await dispatch('save', { model: { ...current, ...normalized, id } });
    return getters.byId(id) || null;
  },

  async fetchAvailableTools({ commit }) {
    try {
      const [catalog, schemaData] = await Promise.all([
        api().get('agent/tools'),
        api().get('agent/tools/schema'),
      ]);
      const schemas = schemaData?.schema || {};
      const tools = normalizeAvailableTools(catalog, schemas);
      commit('setAvailableTools', tools);
      return tools;
    } catch (error) {
      commit('setAvailableTools', []);
      return [];
    }
  },

  async fetchAgentContacts(_, { agentId, status = 'active' }) {
    // api() interceptor already unwraps response.data.data → result is the array
    const result = await api().get(`agent/custom/contacts/${agentId}`, {
      params: { status },
    });
    return Array.isArray(result) ? result : [];
  },

  async enrollContact(_, { agentId, contactId, ifTerminal = 'resume' }) {
    // skipErrorModal: suppress global error modal so caller can show inline eligibility errors
    return api({ skipErrorModal: true }).post('agent/custom/enroll', {
      custom_agent_id: agentId,
      contact_id: contactId,
      if_terminal: ifTerminal,
    });
  },

  async unenrollContact(_, { agentId, contactId }) {
    return api().post('agent/custom/unenroll', {
      custom_agent_id: agentId,
      contact_id: contactId,
    });
  },

  async resetContact(_, { agentId, contactId }) {
    return api().post('agent/custom/reset', {
      custom_agent_id: agentId,
      contact_id: contactId,
    });
  },

  async fetchContactInstances(_, contactId) {
    const result = await api().get(
      `agent/custom/contact-instances/${contactId}`
    );
    return Array.isArray(result) ? result : [];
  },

  async remove({ dispatch, getters }, id) {
    const record = getters.byId(id);
    if (!record) return;
    await dispatch('deleteOne', record);
  },
});

export default module.create();
